import { useLayoutStore } from '@modules/core/store';
import {
  createContext, ReactNode, useEffect
} from 'react';
import useClientOpportunity from '@modules/opportunities/entities/opportunity/query/useClientOpportunity';
import useClientDetails from '@modules/clients/entities/client/query/useClientDetails';
import { Box, Text } from '@huspy/briks-web';
import { Skeleton } from '@mantine/core';
import { Outlet } from '@tanstack/react-router';
import useVaultProgress from '@modules/vault/hooks/queries/useVaultProgress';
import { CaseCreationLayoutRoute } from '@modules/opportunities/presentation/routes';
import { APPLICANT_TYPE, MORTGAGE_USAGE } from '@modules/opportunities/entities/opportunity/const';
import { returnIfSame } from '@shared/utils';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import useCaseCreationStateMachineEngine from './hooks/useCaseCreationStateMachineEngine';
import CaseCreationAsideContent from './features/CaseCreationAsideContent';
import { VaultConfiguration } from './components/VaultConfiguration';

type Props = {
  children?: ReactNode,
};

type ContextType = {
  unlockNextStep: () => void;
  goToNextState: () => void;
  goToPreviousState: () => void;
};

export const CaseCreationContext = createContext<ContextType>({
  unlockNextStep: () => {},
  goToNextState: () => {},
  goToPreviousState: () => {},
});

const CaseCreationLayout = (_: Props) => {
  const { user } = usePersistUser();
  const { setAsideContent, setHeaderContent } = useLayoutStore();
  const { opportunityId } = CaseCreationLayoutRoute.useParams();
  const { data: opportunityData, isLoading, isFetched } = useClientOpportunity(opportunityId);
  const hasSelectedBanks = returnIfSame(
    opportunityData?.opportunity?.vault_config?.selected_banks || [],
    opportunityData?.opportunity.bank_applications.map(
      (item) => item.bank_details.external_id
    ) || []
  ).length > 0;

  const {
    data: vaultProgress,
    isLoading: isLoadingVaultProgress,
    isFetched: isFetchedVaultProgress,
  } = useVaultProgress(opportunityId, hasSelectedBanks);
  const clientId = opportunityData?.opportunity.client_external_id;
  const { data: clientData, isLoading: isClientDataLoading } = useClientDetails(clientId, user?.brokerageId);

  const isOpportunityDataLoading = isLoading || isLoadingVaultProgress;

  const {
    STATE_MACHINE, goToNextState, goToPreviousState, unlockNextStep,
  } = useCaseCreationStateMachineEngine({
    opportunity: opportunityData?.opportunity,
    vaultProgress,
    isEnabled: !!isFetched && (!!isFetchedVaultProgress || !hasSelectedBanks),
    hasSelectedBanks,
  });

  useEffect(() => {
    if (
      opportunityData
      && opportunityData.opportunity.mortgage_usage !== MORTGAGE_USAGE.COMMERCIAL
    ) {
      setHeaderContent(
        <VaultConfiguration
          opportunityId={ opportunityId }
          mainApplicant={ opportunityData?.opportunity.applicants.find(
            (applicant) =>
              applicant.applicant_type === APPLICANT_TYPE.mainApplicant
          ) }
          hasSelectedBanks={ hasSelectedBanks }
          clientId={ clientData?.id }
        />
      );
    }
    return () => {
      setHeaderContent(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderContent, opportunityId, JSON.stringify(opportunityData), JSON.stringify(clientData)]);

  useEffect(() => {
    setAsideContent(
      <CaseCreationAsideContent
        clientData={ clientData }
        isClientDataLoading={ isClientDataLoading }
        isOpportunityDataLoading={ isOpportunityDataLoading }
        opportunityData={ opportunityData }
        stateMachine={ STATE_MACHINE }
      />
    );
    return () => {
      setAsideContent(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientData,
    isClientDataLoading,
    isOpportunityDataLoading,
    opportunityData,
    vaultProgress.documentProgress?.uploaded
  ]);

  return (
    isOpportunityDataLoading ? (
      <Box px='100' py='50'>
        <Skeleton w='100%' height={ 500 } radius='8px' />
      </Box>
    )
      : (
        <Box h='100%'>
          {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
          <CaseCreationContext.Provider value={ {
            goToNextState,
            goToPreviousState,
            unlockNextStep,
          } }
          >
            {opportunityData ? (<Outlet />) : <Text>No opportunity found</Text>}
          </CaseCreationContext.Provider>
        </Box>
      )

  );
};

export default CaseCreationLayout;
